import React, { useEffect, useRef, useState } from 'react';
import { User } from '../../../../model/user/User';
import EmptyState from '../../../../shared/components/placeholder/EmptyState';
import { ErrorToast } from '../../../../utils/toasters';
import { DetailedPerInfluencer } from './components/detaildInfluencer/DetailedPerInfluencer';
import { DetailedPerInfluencerFilter } from './components/detaildInfluencer/DetailedPerInfluencerFilter';
import { DetailedPerInfluencerSkeleton } from './components/detaildInfluencer/DetailedPerInfluencerSkeleton';
import { DetailedPerPlatform } from './components/detaildPlatform/DetailedPerPlatform';
import { CampaignSummary } from './components/summary/CampaignSummary';
import { IFeedbackData } from './components/summary/components/CampaignFeedback';
import { MeasureService } from './service';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { IInfluencerReport } from '../../../../model/measure/IInfluencerReport';
import { useTranslation } from 'react-i18next';
import { IMeasure } from '../../../../model/measure/IMeasure';
import FeedbackAndKpiCard from './components/Feedback&KpiCard';
import { shallowEqual, useSelector } from 'react-redux';
import { IAllStates } from '../../../../store/rootReducer';
import CardLayout from '../../../../shared/components/card/CardLayout';
import { StoreKeeper } from '../../../../store/store';
import { loadingSlice } from '../../../../store/general/loadingSlice';

interface ILocationProps {
    influencerId: number;
}

const SKELETON_COUNT = 2;

export function CampaignMeasure() {
    StoreKeeper.store.dispatch(loadingSlice.actions.setIsLoading(false));

    const { t } = useTranslation();
    const { id } = useParams<'id'>();
    const { state } = useLocation();
    const navigate = useNavigate();
    const [isSummaryLoading, setIsSummaryLoading] = useState(true);
    const [selectedInfluencerId, setSelectedInfluencerId] = useState({
        influencerId: '',
    });
    const [influencerData, setInfluencerData] = useState<IInfluencerReport[]>([]);
    const [detailedPerPlatformData, setDetailedPerPlatformData] = useState<IInfluencerReport[]>([]);
    const [isDetailedLoading, setIsDetailedLoading] = useState(true);
    const lastDetailedKeyRef = useRef<string>();
    const [updateReview, setUpdateReview] = useState(false);
    const [feedbackData, setFeedbackData] = useState<IFeedbackData | undefined>(undefined);
    const [influencers, setInfluencers] = useState<User[]>();
    const [selectedPlatformInfluencer, setSelectedPlatformInfluencer] = useState({ id: '' });
    const [summaryData, setSummaryData] = useState<IMeasure>();
    const [triggerSummaryData, setTriggerSummaryData] = useState(false);
    const { isAclLoaded } = useSelector((state: IAllStates) => state.selectedCampaign, shallowEqual);
    const [isPlatformLoading, setIsPlatformLoading] = useState(true);
    const lastPlatformKeyRef = useRef<string>();
    useEffect(() => {
        if (!id) {
            return;
        }
        const queryKey = `${id}-${selectedPlatformInfluencer?.id}`;
        if (lastPlatformKeyRef.current !== queryKey) {
            lastPlatformKeyRef.current = queryKey;
            setIsPlatformLoading(true);
        }

        let isCurrent = true;
        MeasureService.getInfluencersData({ id: selectedPlatformInfluencer?.id }, +id)
            .then(response => {
                if (!isCurrent) return;
                setInfluencerData(response);
                (state as ILocationProps)?.influencerId && navigate('.', { replace: true, state: undefined });
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsPlatformLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [updateReview, selectedPlatformInfluencer, id]);

    useEffect(() => {
        if (!id) {
            return;
        }

        const influencerId = String(
            (state as ILocationProps)?.influencerId ?? selectedInfluencerId?.influencerId ?? ''
        );
        const queryKey = `${id}-${influencerId}`;
        if (lastDetailedKeyRef.current !== queryKey) {
            lastDetailedKeyRef.current = queryKey;
            setIsDetailedLoading(true);
        }

        let isCurrent = true;
        MeasureService.getInfluencersData({ id: influencerId }, +id)
            .then(response => {
                if (!isCurrent) return;
                setDetailedPerPlatformData(response);
                (state as ILocationProps)?.influencerId && navigate('.', { replace: true, state: undefined });
            })
            .catch(error => ErrorToast(error))
            .finally(() => {
                if (isCurrent) setIsDetailedLoading(false);
            });

        return () => {
            isCurrent = false;
        };
    }, [selectedInfluencerId, updateReview, id]);

    useEffect(() => {
        if (!!(state as ILocationProps)?.influencerId) {
            setSelectedInfluencerId(prev => ({
                ...prev,
                influencerId: String((state as ILocationProps)?.influencerId),
            }));
        }
    }, [(state as ILocationProps)?.influencerId]);

    useEffect(() => {
        if (!id) {
            return;
        }
        MeasureService.getMeasureFeedback(+id)
            .then(response => {
                setFeedbackData(response);
            })
            .catch(err => ErrorToast(err));
    }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }
        MeasureService.getInfluencers(+id)
            .then(response => {
                setInfluencers(response);
            })
            .catch(err => ErrorToast(err));
    }, [id]);

    useEffect(() => {
        if (!id) {
            return;
        }
        MeasureService.getSummaryData(+id)
            .then(response => {
                setSummaryData(response);
            })
            .catch(error => ErrorToast(error))
            .finally(() => setIsSummaryLoading(false));
    }, [triggerSummaryData, id]);

    return isAclLoaded ? (
        <div className="pb-5 mb-5 mb-md-0">
            {feedbackData?.show && (
                <FeedbackAndKpiCard
                    setTriggerSummaryData={setTriggerSummaryData}
                    summaryData={summaryData}
                    feedbackData={feedbackData}
                    setFeedbackData={setFeedbackData}
                />
            )}
            <CampaignSummary {...summaryData} isLoading={isDetailedLoading} />
            <DetailedPerPlatform
                influencerData={influencerData}
                influencers={influencers}
                selectedPlatformId={selectedPlatformInfluencer}
                setSelectedPlatformId={setSelectedPlatformInfluencer}
                isLoading={isPlatformLoading}
            />
            <DetailedPerInfluencerFilter
                setSelectedInfluencerId={setSelectedInfluencerId}
                influencers={influencers}
                selectedInfluencerId={+selectedInfluencerId.influencerId}
            />
            {isDetailedLoading ? (
                Array.from({ length: SKELETON_COUNT }).map((_, i) => <DetailedPerInfluencerSkeleton key={i} />)
            ) : !!detailedPerPlatformData?.length ? (
                detailedPerPlatformData.map((influencer, index) => (
                    <DetailedPerInfluencer
                        key={index}
                        setUpdateReview={setUpdateReview}
                        updateReview={updateReview}
                        influencer={influencer}
                    />
                ))
            ) : (
                <CardLayout>
                    <EmptyState
                        addClass="col-lg-5 col-8 text-center m-auto"
                        desc={t('brand.campaign.measure.empty_detailed')}
                    />
                </CardLayout>
            )}
        </div>
    ) : null;
}
